import React, {useEffect, useMemo, useRef, useState} from "react";
import * as d3 from "d3";
import { zoom } from "d3-zoom";
import {
  ZoomTransform,
  scaleLinear,
  select,
  axisLeft,
  axisBottom,
  Selection,
} from "d3";
import useResizeObserver from "../hooks/useRezizeObserver";
import { ppmToHexColor } from "../utils/ppm-to-hex-color";
import PPPMErrorLegend from "./PPMErrorLegend";
import {getEmptyZones} from "../utils/get-empty-zones";

type Props = {
  masses: { int: number; mz: number }[];
  min?: number;
  max?: number;
  matchedProfile?: {
    mw: number;
    subunit: string;
    matched?: boolean;
    intensity?: number;
    mz_error?: number;
  }[];
};

const MARGIN = { top: 10, right: 30, bottom: 30, left: 60 };


const getHighestInt = (listInt: number[]) =>
  Math.max(...listInt);

export const SpectrumPlot: React.FC<Props> = (props) => {
  const masses = useMemo(() => props.masses.filter(x => x.mz >= 3000 && x.mz <= 20000), [props.masses]);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const dimensions = useResizeObserver(wrapperRef);
  const [currentZoomState, setCurrentZoomState] = useState<ZoomTransform>();

  const { width = 0, height = 0 } = dimensions! || { width: 0, height: 0 }; // || wrapperRef !== null && wrapperRef.current!.getBoundingClientRect();

  const boundsWidth = width - MARGIN.right - MARGIN.left;
  const boundsHeight = height - MARGIN.top - MARGIN.bottom;

  const highestSubunitInt = useMemo(() => getHighestInt(props.matchedProfile?.map(({intensity}) => intensity ?? 0) || [0]), [props.matchedProfile]);
  const highestInt = useMemo(() => getHighestInt(masses.map(({int}) => int)), [masses]);
  const subunitIntFactor = highestInt / highestSubunitInt;
  const matchedProfile = useMemo(() => props.matchedProfile?.map(({intensity, ...rest}) => ({...rest, intensity: intensity ? intensity * subunitIntFactor : 0})), [props.matchedProfile, subunitIntFactor]);
  const emptyzonesData = useMemo(() => getEmptyZones(masses), [masses]);

  // memoized ppmToHexColor function

    const ppmToHexColorMemoized = useMemo(() => ppmToHexColor, []);

    // will be called initially and on every data change
  useEffect(() => {
    if (!svgRef.current) return;
    const svg = select(svgRef.current);
    const svgContent = svg.select(".content");

    // scales + line generator
    const xScale = scaleLinear().domain([3000, 20000]).range([0, boundsWidth]);

    if (currentZoomState) {
      const newXScale = currentZoomState.rescaleX(xScale);
      xScale.domain(newXScale.domain());
    }

    const yScaleMax = highestInt;

    const yScale = scaleLinear()
      .domain([0, yScaleMax])
      .range([boundsHeight, 0]);

    const visiblePeaks = masses.filter(
        ({ mz }) => mz >= xScale.domain()[0] && mz <= xScale.domain()[1]
    );

    const visibleSubunits = matchedProfile?.filter(
        ({ mw }) => mw >= xScale.domain()[0] && mw <= xScale.domain()[1]
    ) || [];

    // create or select element for min zone
    const minZone = svgContent
      .select<SVGRectElement>(".minZone")
      .empty()
      ? svgContent.append<SVGRectElement>("rect").attr("class", "minZone")
      : svgContent.select<SVGRectElement>(".minZone");

    // create or select element for max zone
    const maxZone = svgContent
      .select<SVGRectElement>(".maxZone")
      .empty()
      ? svgContent.append<SVGRectElement>("rect").attr("class", "maxZone")
      : svgContent.select<SVGRectElement>(".maxZone");


    // Create or select groups for emptyzones, subunits and peaks
    const emptyzonesGroup: d3.Selection<SVGGElement, unknown, null, undefined> = svgContent
        .select<SVGGElement>("g.emptyzonesGroup")
        .empty()
        ? svgContent.append<SVGGElement>("g").attr("class", "emptyzonesGroup")
        : svgContent.select<SVGGElement>("g.emptyzonesGroup");

    const subunitsGroup: d3.Selection<SVGGElement, unknown, null, undefined> = svgContent
        .select<SVGGElement>("g.subunitsGroup")
        .empty()
        ? svgContent.append<SVGGElement>("g").attr("class", "subunitsGroup")
        : svgContent.select<SVGGElement>("g.subunitsGroup");


    const peaksGroup: d3.Selection<SVGGElement, unknown, null, undefined> =
        svgContent.select<SVGGElement>("g.peaksGroup").empty()
            ? svgContent.append<SVGGElement>("g").attr("class", "peaksGroup")
            : svgContent.select<SVGGElement>("g.peaksGroup");

    // spectrum peaks
    const emptyzones = emptyzonesGroup
        .selectAll<SVGLineElement, { start: number; end: number }>(".emptyzones")
        .data(emptyzonesData);

    // add min zone from 3000 to props.min
    if(props.min && props.min >= 3000) {
      minZone
          .attr("x", xScale(3000))
          .attr("y", 0)
          .attr("width", xScale(props.min || 3000) - xScale(3000))
          .attr("height", boundsHeight)
          .attr("fill", "rgba(200, 200, 200, 0.5)")
    }

    // add max zone from props.max to 20000
    if(props.max && props.max <= 20000) {
      maxZone
          .attr("x", xScale(props.max || 20000))
          .attr("y", 0)
          .attr("width", xScale(20000) - xScale(props.max || 20000))
          .attr("height", boundsHeight)
          .attr("fill", "rgba(200, 200, 200, 0.5)")

    }


    emptyzones.exit().remove();

    // emptyzones
    emptyzones
        .enter()
        .append("line")
        .attr("class", "emptyzones")
        .merge(emptyzones)
        .attr("x1", ({ start }) => Math.round(xScale(start + 5)))
        .attr("x2", ({ end }) => Math.round(xScale(end - 5)))
        .attr("y1", yScale(0))
        .attr("y2", yScale(0))
        .attr("stroke", "red")
        .attr("stroke-width", 4)
        .attr("data-start", ({ start }) => start)
        .attr("data-end", ({ end }) => end);



    // subunits
    const subunits = subunitsGroup
        .selectAll<SVGLineElement, { mw: number; subunit: string; intensity: number, mz_error: number }>(".subunits")
        .data(visibleSubunits.filter(({ mw }) => mw < 20000));

    subunits.exit().remove();

    subunits
        .enter()
        .append("line")
        .attr("class", "subunits")
        .merge(subunits)
        .attr("stroke", ({ matched, mz_error = 0 }) => (matched ? ppmToHexColorMemoized(mz_error) : "grey"))
        .attr("stroke-width", 8)
        .attr("x1", ({ mw }) => Math.round(xScale(mw)))
        .attr("x2", ({ mw }) => Math.round(xScale(mw)))
        .attr("y1", ({ intensity }) => intensity ? Math.round(yScale(intensity)) : yScale(yScaleMax))
        .attr("y2", yScale(0))
        .attr("opacity", ({ matched }) => (matched ? 0.6 : 0.2))
        .attr("data-subunit", ({ subunit }) => subunit)
        .attr("data-mz_error", ({ mz_error }) => mz_error || 0);

    // spectrum peaks
    const peaks = peaksGroup
        .selectAll<SVGLineElement, { mz: number; int: number }>(".allPeaks")
        .data(visiblePeaks.filter(({ mz }) => mz > 3000), (d) => d.mz);

    peaks.exit().remove();

    peaks
        .enter()
        .append("line")
        .attr("class", "allPeaks")
        .merge(peaks)
        .attr("x1", ({ mz }) => Math.round(xScale(mz)))
        .attr("x2", ({ mz }) => Math.round(xScale(mz)))
        .attr("y1", ({ int }) => Math.round(yScale(int)))
        .attr("y2", yScale(0))
        .attr("stroke", "#282c32")
        .attr("stroke-width", 3)
        .attr("data-mz", ({ mz }) => Math.round(mz * 10) / 10)
        .attr("data-int", ({ int }) => Math.round(int));


    // hover overlays: lower layer sits between subunits and peaks (so peaks stay visible
    // on top of a subunit-highlight), upper layer sits on top for peak/emptyzone highlights.
    const lowerOverlayGroup: d3.Selection<SVGGElement, unknown, null, undefined> = svgContent
        .select<SVGGElement>("g.hoverOverlayLower")
        .empty()
        ? svgContent
            .append<SVGGElement>("g")
            .attr("class", "hoverOverlayLower")
            .style("pointer-events", "none")
        : svgContent.select<SVGGElement>("g.hoverOverlayLower");

    const upperOverlayGroup: d3.Selection<SVGGElement, unknown, null, undefined> = svgContent
        .select<SVGGElement>("g.hoverOverlayUpper")
        .empty()
        ? svgContent
            .append<SVGGElement>("g")
            .attr("class", "hoverOverlayUpper")
            .style("pointer-events", "none")
        : svgContent.select<SVGGElement>("g.hoverOverlayUpper");

    // enforce draw order: emptyzones, subunits, lowerOverlay, peaks, upperOverlay
    emptyzonesGroup.raise();
    subunitsGroup.raise();
    lowerOverlayGroup.raise();
    peaksGroup.raise();
    upperOverlayGroup.raise();

    const ensureHighlightLine = (group: d3.Selection<SVGGElement, unknown, null, undefined>) =>
        group.select<SVGLineElement>("line.highlight").empty()
            ? group.append<SVGLineElement>("line").attr("class", "highlight").style("display", "none")
            : group.select<SVGLineElement>("line.highlight");

    const lowerHighlight = ensureHighlightLine(lowerOverlayGroup);
    const upperHighlight = ensureHighlightLine(upperOverlayGroup);
    lowerHighlight.style("display", "none");
    upperHighlight.style("display", "none");

    const showHighlightOn = (
        highlight: d3.Selection<SVGLineElement, unknown, null, undefined>,
        target: SVGLineElement,
        stroke: string,
        strokeWidth: number
    ) => {
      highlight
          .attr("x1", target.getAttribute("x1"))
          .attr("x2", target.getAttribute("x2"))
          .attr("y1", target.getAttribute("y1"))
          .attr("y2", target.getAttribute("y2"))
          .attr("stroke", stroke)
          .attr("stroke-width", strokeWidth)
          .attr("opacity", 1)
          .style("display", "block");
    };
    const hideHighlight = () => {
      lowerHighlight.style("display", "none");
      upperHighlight.style("display", "none");
    };
    const showTooltip = (event: MouseEvent, html: string) => {
      const tooltipDiv = tooltipRef.current;
      if (!tooltipDiv) return;
      d3.select(tooltipDiv)
          .style("opacity", "1")
          .style("visibility", "visible")
          .style("top", event.pageY - 30 + "px")
          .style("left", event.pageX + 10 + "px")
          .html(html);
    };
    const hideTooltip = () => {
      const tooltipDiv = tooltipRef.current;
      if (!tooltipDiv) return;
      d3.select(tooltipDiv).style("opacity", "0").style("visibility", "hidden");
    };

    emptyzonesGroup
        .on("mousemove", (event) => {
          const t = event.target as SVGElement;
          if (t.tagName !== "line" || !t.classList.contains("emptyzones")) {
            hideHighlight();
            hideTooltip();
            return;
          }
          const line = t as SVGLineElement;
          showHighlightOn(upperHighlight, line, "#d3df00", 4);
          showTooltip(
              event,
              `emptyzone from ${Number(line.dataset.start).toLocaleString("de-CH")} m/z to ${Number(line.dataset.end).toLocaleString("de-CH")} m/z`
          );
        })
        .on("mouseleave", () => {
          hideHighlight();
          hideTooltip();
        });

    subunitsGroup
        .on("mousemove", (event) => {
          const t = event.target as SVGElement;
          if (t.tagName !== "line" || !t.classList.contains("subunits")) {
            hideHighlight();
            hideTooltip();
            return;
          }
          const line = t as SVGLineElement;
          showHighlightOn(lowerHighlight, line, "#DC3546", 8);
          const mz_error = Number(line.dataset.mz_error);
          showTooltip(
              event,
              `${line.dataset.subunit} ${mz_error !== 0 ? `| ${Math.round(mz_error)} ppm error` : ""}`
          );
        })
        .on("mouseleave", () => {
          hideHighlight();
          hideTooltip();
        });

    peaksGroup
        .on("mousemove", (event) => {
          const t = event.target as SVGElement;
          if (t.tagName !== "line" || !t.classList.contains("allPeaks")) {
            hideHighlight();
            hideTooltip();
            return;
          }
          const line = t as SVGLineElement;
          showHighlightOn(upperHighlight, line, "#DC3546", 3);
          showTooltip(
              event,
              `m/z: ${Number(line.dataset.mz).toLocaleString("de-CH")} | intensity: ${Number(line.dataset.int).toLocaleString("de-CH")}`
          );
        })
        .on("mouseleave", () => {
          hideHighlight();
          hideTooltip();
        });


    // axes
    const xAxis = axisBottom(xScale);

    svg
      .select<SVGSVGElement>(".x-axis")
      .attr("transform", `translate(0, ${boundsHeight})`)
      .call(xAxis);

    const yAxis = axisLeft(yScale);
    svg.select<SVGSVGElement>(".y-axis").call(yAxis);

    // zoom
    const zoomBehavior = zoom()
      .scaleExtent([1, 40])
      .translateExtent([
        [0, 0],
        [width, height],
      ])
      .on("zoom", (event: { transform: any; currentTarget: any; }) => {
        const zoomState = event.transform;
        setCurrentZoomState(zoomState);

        // remove tooltip
        const tooltipDiv = tooltipRef.current;
        d3.select(event.currentTarget).attr("stroke", "#282c32");
        if (tooltipDiv)
          return d3
            .select(tooltipDiv)
            .style("opacity", "0")
            .style("visibility", "hidden");
      });

    // todo clen up?
    svg.call((selection) =>
      zoomBehavior(
        selection as unknown as Selection<Element, unknown, any, any>
      )
    );
  }, [
    currentZoomState,
    props,
    dimensions,
    boundsWidth,
    boundsHeight,
    height,
    width,
      emptyzonesData,
      highestInt,
      matchedProfile,
      ppmToHexColorMemoized
  ]);

  return (
    <div ref={wrapperRef} style={{ marginBottom: "2rem", height: "400px" }}>
      <div
        className="tooltip"
        ref={tooltipRef}
        style={{ background: "white", padding: "2px 5px", pointerEvents: "none" }}
      />
      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{ display: "inline-block" }}
      >
        <defs>
          <clipPath id="spectrum-plot">
            <rect x="0" y="0" width="100%" height="100%" />
          </clipPath>
        </defs>
        <g
          className="content"
          clipPath={`url(#spectrum-plot`}
          width={boundsWidth}
          height={boundsHeight}
          transform={`translate(${[MARGIN.left, MARGIN.top].join(",")})`}
        />
        <g
          width={boundsWidth}
          height={boundsHeight}
          transform={`translate(${[MARGIN.left, MARGIN.top].join(",")})`}
        >
          <g className="x-axis" />
          <g className="y-axis" />
        </g>
      </svg>
      {matchedProfile && matchedProfile.length > 0 && <PPPMErrorLegend />}

    </div>
  );
};
